/**
 * Benutzerverwaltung — the `user.admin` screen over `/api/admin/*`.
 *
 * Three panels, in the order an administrator needs them:
 *   1. the user table with create / edit / delete,
 *   2. the role → permission matrix (server-authoritative) so it is visible what
 *      an auditor may actually do,
 *   3. the lazily loaded session audit view (`session.admin`).
 *
 * Notes that shaped the code:
 *   - `GET /api/admin/users` and `GET /api/admin/roles` are loaded with
 *     `allSettled`: a failing matrix must not blank the user table, and vice
 *     versa. The matrix then falls back to the mirrored `ROLE_PERMISSIONS` and
 *     says so.
 *   - Every mutating control is gated on `has('user.admin')` even though the
 *     route already requires it — an auditor who reaches this component through
 *     a stale bundle sees data and disabled buttons, never a live action.
 *   - Server errors are German and are shown VERBATIM, including the 409
 *     last-active-admin refusal, which is surfaced inside the dialog that caused
 *     it so the administrator can back out without losing input.
 *   - Editing your own account can change your own roles, so the auth context is
 *     refreshed afterwards; the navigation reacts immediately.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import { Banner } from '../components/Banner';
import { Chip, ChipList } from '../components/Chip';
import DataTable from '../components/DataTable';
import type { DataTableColumn } from '../components/DataTable';
import { KpiRow, type KpiItem } from '../components/KpiTile';
import PageHeader from '../components/PageHeader';
import { PageSpinner } from '../components/Spinner';
import { useToast } from '../components/Toast';
import { errorMessage, fetchAdminRoles, fetchAdminUsers } from '../lib/api';
import { useAuth } from '../lib/auth';
import { countLabel, fmtDate, fmtValue } from '../lib/format';
import { missingPermissionTitle } from '../lib/permissions';
import {
  ROLES,
  ROLE_DESCRIPTIONS,
  ROLE_LABELS,
  ROLE_PERMISSIONS,
} from '../lib/types';
import type { AdminUser, KpiCounter, Role, RoleInfo } from '../lib/types';
import DeleteUserModal from './admin/DeleteUserModal';
import RolesMatrix from './admin/RolesMatrix';
import SessionsPanel from './admin/SessionsPanel';
import UserFormModal from './admin/UserFormModal';

import './admin/ops.css';

const NO_USER_ADMIN = missingPermissionTitle('user.admin');

/** Which dialog is open, and for which row. */
type Dialog =
  | { kind: 'create' }
  | { kind: 'edit'; user: AdminUser }
  | { kind: 'delete'; user: AdminUser }
  | null;

function roleLabel(role: Role | string): string {
  const labels: Readonly<Record<string, string | undefined>> = ROLE_LABELS;
  return labels[role] ?? role;
}

/** The mirrored matrix, shaped like the server response — used as a fallback. */
function mirroredRoles(): readonly RoleInfo[] {
  return ROLES.map((name) => ({
    name,
    description: ROLE_DESCRIPTIONS[name],
    permissions: [...ROLE_PERMISSIONS[name]],
  }));
}

/** Status filter of the account list; the KPI tiles set the same filter.
 *  Administrators and auditors count active accounts only, like the tiles. */
type UserFilter = 'alle' | 'aktiv' | 'admin' | 'auditor' | 'passwort';

const USER_FILTERS: Readonly<Record<UserFilter, { label: string; match: (row: AdminUser) => boolean }>> = {
  alle: { label: 'Alle Konten', match: () => true },
  aktiv: { label: 'Aktiv', match: (row) => row.active },
  admin: { label: 'Administratoren', match: (row) => row.active && row.roles.includes('admin') },
  auditor: { label: 'Auditoren', match: (row) => row.active && row.roles.includes('auditor') },
  passwort: { label: 'Passwortwechsel offen', match: (row) => row.must_change_password },
};

export function AdminUsersPage(): ReactNode {
  const { user: currentUser, has, refresh } = useAuth();
  const toast = useToast();

  const [users, setUsers] = useState<readonly AdminUser[] | null>(null);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [roles, setRoles] = useState<readonly RoleInfo[] | null>(null);
  const [rolesError, setRolesError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<UserFilter>('alle');
  const [dialog, setDialog] = useState<Dialog>(null);

  const canManage = has('user.admin');
  const canSeeSessions = has('session.admin');

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    // allSettled: one failing endpoint must not take the other panel down.
    const [usersResult, rolesResult] = await Promise.allSettled([
      fetchAdminUsers(),
      fetchAdminRoles(),
    ]);
    if (usersResult.status === 'fulfilled') {
      setUsers(usersResult.value.users);
      setUsersError(null);
    } else {
      setUsersError(errorMessage(usersResult.reason));
    }
    if (rolesResult.status === 'fulfilled') {
      setRoles(rolesResult.value.roles);
      setRolesError(null);
    } else {
      setRoles(null);
      setRolesError(errorMessage(rolesResult.reason));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const roleInfos = useMemo<readonly RoleInfo[]>(() => roles ?? mirroredRoles(), [roles]);

  const filtered = useMemo<readonly AdminUser[]>(() => {
    const rows = (users ?? []).filter((row) => USER_FILTERS[status].match(row));
    const needle = search.trim().toLowerCase();
    if (needle === '') return rows;
    return rows.filter((row) => {
      const haystack = [row.email, row.name ?? '', ...row.roles.map(roleLabel), ...row.roles]
        .join(' ')
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [users, search, status]);

  const counters = useMemo<readonly KpiCounter[]>(() => {
    const rows = users ?? [];
    const active = rows.filter((row) => row.active);
    const admins = active.filter((row) => row.roles.includes('admin'));
    const auditors = active.filter((row) => row.roles.includes('auditor'));
    const pending = rows.filter((row) => row.must_change_password);
    return [
      { label: 'Benutzer', value: rows.length, tone: 'neutral' },
      {
        label: 'Aktiv',
        value: active.length,
        tone: 'neutral',
        title: 'Konten, die sich anmelden dürfen',
      },
      {
        label: 'Administratoren',
        value: admins.length,
        // Exactly one active admin means the server will refuse to remove them.
        tone: admins.length === 0 ? 'alert' : admins.length === 1 ? 'warn' : 'good',
        title:
          admins.length === 1
            ? 'Der letzte aktive Administrator kann nicht deaktiviert, herabgestuft oder gelöscht werden.'
            : 'Aktive Konten mit der Rolle Administrator',
      },
      { label: 'Auditoren', value: auditors.length, tone: 'neutral' },
      {
        label: 'Passwortwechsel offen',
        value: pending.length,
        tone: pending.length > 0 ? 'warn' : 'good',
        title: 'Konten, die beim nächsten Login das Passwort ändern müssen',
      },
    ];
  }, [users]);

  // Each tile is an option of the status select; tile and select show the
  // same filter, a second click on the tile goes back to all accounts.
  const kpiTiles = useMemo((): KpiItem[] => {
    const targets: Readonly<Record<string, UserFilter>> = {
      Benutzer: 'alle',
      Aktiv: 'aktiv',
      Administratoren: 'admin',
      Auditoren: 'auditor',
      'Passwortwechsel offen': 'passwort',
    };
    return counters.map((counter) => {
      const target = targets[counter.label];
      if (!target) return counter;
      const active = status === target && search.trim() === '';
      return {
        ...counter,
        ariaLabel: `${counter.label}: ${counter.value} anzeigen`,
        selected: active,
        controls: 'users-results',
        onClick: () => { setStatus(active ? 'alle' : target); setSearch(''); },
      };
    });
  }, [counters, search, status]);

  const isSelf = useCallback(
    (row: AdminUser): boolean => currentUser !== null && row.id === currentUser.id,
    [currentUser],
  );

  const onSaved = useCallback(
    (saved: AdminUser, message: string): void => {
      setDialog(null);
      toast.success(message);
      void load();
      // Own roles may have changed — re-read the authoritative session user so
      // the navigation and every permission gate follow immediately.
      if (currentUser !== null && saved.id === currentUser.id) void refresh();
    },
    [toast, load, currentUser, refresh],
  );

  const onDeleted = useCallback(
    (deleted: AdminUser, message: string): void => {
      setDialog(null);
      toast.success(message);
      void load();
      if (currentUser !== null && deleted.id === currentUser.id) void refresh();
    },
    [toast, load, currentUser, refresh],
  );

  const columns = useMemo<readonly DataTableColumn<AdminUser>[]>(
    () => [
      {
        key: 'email',
        header: 'E-Mail',
        sortValue: (row) => row.email,
        render: (row) => (
          <>
            <div className="kunde-name">{row.email}</div>
            {isSelf(row) ? (
              <div style={{ marginTop: 'var(--space-1)' }}>
                <Chip tone="info">Ihr Konto</Chip>
              </div>
            ) : null}
          </>
        ),
      },
      {
        key: 'name',
        header: 'Name',
        sortValue: (row) => row.name,
        render: (row) => fmtValue(row.name),
      },
      {
        key: 'roles',
        header: 'Rollen',
        sortValue: (row) => row.roles.join(','),
        render: (row) =>
          row.roles.length === 0 ? (
            <Chip tone="warn" title="Ohne Rolle sieht das Konto keine Inhalte.">
              keine Rolle
            </Chip>
          ) : (
            <ChipList>
              {row.roles.map((role) => (
                <Chip key={role} tone={role === 'admin' ? 'info' : 'neutral'}>
                  {roleLabel(role)}
                </Chip>
              ))}
            </ChipList>
          ),
      },
      {
        key: 'status',
        header: 'Status',
        sortValue: (row) => row.active,
        render: (row) => (
          <ChipList>
            <Chip tone={row.active ? 'good' : 'alert'}>{row.active ? 'aktiv' : 'inaktiv'}</Chip>
            {row.must_change_password ? (
              <Chip tone="warn" title="Beim nächsten Login ist eine Passwortänderung erforderlich.">
                Passwortwechsel
              </Chip>
            ) : null}
          </ChipList>
        ),
      },
      {
        key: 'last_login',
        header: 'Letzter Login',
        sortValue: (row) => row.last_login_at,
        render: (row) =>
          row.last_login_at === null ? (
            <span className="muted">noch nie</span>
          ) : (
            <span>{fmtDate(row.last_login_at)}</span>
          ),
      },
      {
        key: 'created',
        header: 'Erstellt',
        sortValue: (row) => row.created_at,
        render: (row) => <span>{fmtDate(row.created_at)}</span>,
      },
      {
        key: 'actions',
        header: 'Aktionen',
        align: 'right',
        render: (row) => (
          <div className="row" style={{ justifyContent: 'flex-end', gap: 6 }}>
            <button
              type="button"
              className="ghost small"
              onClick={() => setDialog({ kind: 'edit', user: row })}
              disabled={!canManage}
              title={canManage ? undefined : NO_USER_ADMIN}
            >
              Bearbeiten
            </button>
            <button
              type="button"
              className="danger small"
              onClick={() => setDialog({ kind: 'delete', user: row })}
              disabled={!canManage}
              title={canManage ? undefined : NO_USER_ADMIN}
            >
              Löschen
            </button>
          </div>
        ),
      },
    ],
    [canManage, isSelf],
  );

  if (loading && users === null && usersError === null) {
    return <PageSpinner text="Benutzer werden geladen…" />;
  }

  return (
    <>
      <PageHeader
        title="Benutzerverwaltung"
        actions={
          <button
            type="button"
            className="primary"
            onClick={() => setDialog({ kind: 'create' })}
            disabled={!canManage}
            title={canManage ? undefined : NO_USER_ADMIN}
          >
            Benutzer anlegen
          </button>
        }
      />

      {!canManage ? (
        <Banner kind="warn">
          Nur Lesezugriff: zum Anlegen, Ändern und Löschen von Konten wird die Berechtigung{' '}
          <code className="mono">user.admin</code> benötigt.
        </Banner>
      ) : null}

      {usersError !== null ? (
        <Banner kind="error" sub="Laden Sie die Seite neu.">
          {usersError}
        </Banner>
      ) : null}

      <KpiRow counters={kpiTiles} />

      <section id="users-results" className="card">
        <div className="card-head table-head">
          <h2>Benutzer</h2>
        </div>
        <div className="table-toolbar">
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Suche nach E-Mail, Name oder Rolle…"
            aria-label="Benutzer suchen"
          />
          <select
            value={status}
            onChange={(event) => setStatus(event.target.value as UserFilter)}
            aria-label="Nach Status filtern"
          >
            {(Object.keys(USER_FILTERS) as UserFilter[]).map((key) => (
              <option key={key} value={key}>
                {USER_FILTERS[key].label}
              </option>
            ))}
          </select>
          <div className="table-toolbar-options">
            {status !== 'alle' || search.trim() !== '' ? (
              <button type="button" className="link" onClick={() => { setStatus('alle'); setSearch(''); }}>
                Filter zurücksetzen
              </button>
            ) : null}
            <span className="table-count" aria-live="polite">
              {filtered.length === (users?.length ?? 0)
                ? countLabel(users?.length ?? 0, 'Konto', 'Konten')
                : `${filtered.length} von ${countLabel(users?.length ?? 0, 'Konto', 'Konten')}`}
            </span>
          </div>
        </div>
        <DataTable
          rows={filtered}
          columns={columns}
          rowKey={(row) => row.id}
          defaultSort={{ columnKey: 'email', direction: 'asc' }}
          mobileBreakpoint={960}
          pageSize={10}
          paginationResetKey={`${status}|${search}`}
          resetPageOnRowsChange={false}
          caption="Lokale Benutzerkonten"
          rowClassName={(row) => (row.active ? undefined : 'muted')}
          emptyText={
            users === null
              ? 'Benutzer konnten nicht geladen werden.'
              : (users?.length ?? 0) === 0
                ? 'Keine Benutzer vorhanden.'
                : search.trim() !== ''
                  ? `Kein Konto passt zu „${search.trim()}“.`
                  : 'Kein Konto passt zum Filter.'
          }
        />
      </section>

      <RolesMatrix
        roles={roleInfos}
        fallback={roles === null}
        fallbackReason={rolesError}
        ownRoles={currentUser?.roles ?? []}
      />

      <SessionsPanel allowed={canSeeSessions} ownEmail={currentUser?.email ?? null} />

      {dialog?.kind === 'create' ? (
        <UserFormModal
          mode="create"
          user={null}
          availableRoles={roleInfos}
          isSelf={false}
          onClose={() => setDialog(null)}
          onSaved={onSaved}
        />
      ) : null}

      {dialog?.kind === 'edit' ? (
        <UserFormModal
          mode="edit"
          user={dialog.user}
          availableRoles={roleInfos}
          isSelf={isSelf(dialog.user)}
          onClose={() => setDialog(null)}
          onSaved={onSaved}
        />
      ) : null}

      {dialog?.kind === 'delete' ? (
        <DeleteUserModal
          user={dialog.user}
          isSelf={isSelf(dialog.user)}
          onClose={() => setDialog(null)}
          onDeleted={onDeleted}
        />
      ) : null}
    </>
  );
}

export default AdminUsersPage;
